import { createContext, useContext, useState, useEffect } from 'react';
import { getClientIdFromPath } from '../lib/utils';

/**
 * Client Context
 *
 * Manages multi-client configuration and state
 * Supports URL-based client routing: /{clientId}
 *
 * **A failed config fetch is an error, never a different tenant.** This used to
 * catch the failure and substitute `getStaticClientConfig`, a hardcoded table
 * holding three tenants that are not the nineteen in the database. For any
 * tenant absent from it — npas included — a dead API rendered generic defaults:
 * the wrong name, the wrong colour, the wrong concierge. It looked like a
 * styling bug and cost two separate diagnoses in one evening before anybody
 * suspected the API.
 *
 * That is the no-silent-fallback rule sitting unenforced in the frontend. The
 * config decides what a visitor is told the organisation is called; getting it
 * wrong quietly is worse than showing nothing, because nothing is legible as a
 * fault and a wrong name is not.
 */
const ClientContext = createContext(null);

export function ClientProvider({ children }) {
  const [clientId, setClientId] = useState(null);
  const [clientConfig, setClientConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Set when the config could not be loaded. Config and error are mutually
  // exclusive: a consumer reading a config never has to wonder whether it is
  // this tenant's.
  const [configError, setConfigError] = useState(null);

  // Get client ID from URL on mount
  useEffect(() => {
    const id = getClientIdFromPath();
    setClientId(id);
    loadClientConfig(id);
  }, []);

  // Load client-specific configuration from API (database-driven)
  async function loadClientConfig(id) {
    try {
      setIsLoading(true);
      setConfigError(null);

      // Fetch from API endpoint (which reads from database)
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/config/${id}`);

      if (response.ok) {
        const config = await response.json();
        setClientConfig({
          clientId: config.client_id,
          orgName: config.org_name,
          orgShortName: config.org_short_name || config.org_name,
          // The tenant's language and clock, not the visitor's browser's. Anything
          // this app formats itself must use them.
          locale: config.locale || null,
          timezone: config.timezone || null,
          orgSlug: config.org_slug,
          tagline: config.tagline,
          conciergeName: config.concierge_name || 'Conci',
          primaryColor: config.primary_color || '#1e40af',
          logoUrl: config.logo_url,
          partnerLogoUrl: config.partner_logo_url,
          heading: config.heading,
          subheading: config.subheading,
          askPlaceholder: config.ask_placeholder,
          drawer: config.drawer || null,
          liveDemoPage: config.live_demo_page || null,
          knowledgeTrust: config.knowledge_trust || null,
          autocomplete: config.autocomplete || false,
          memory: config.memory || false,
          authSessionTtlDays: Number.isFinite(Number(config.auth_session_ttl_days))
            ? Number(config.auth_session_ttl_days)
            : 60,
          rememberLogin: config.remember_login !== false,
        });
      } else {
        throw new Error(`Config API returned ${response.status} for client "${id}"`);
      }
    } catch (error) {
      // Loudly, and with nothing put in its place. The config is cleared as
      // well as the error set, so no consumer can read a stale or borrowed one.
      console.error(`Failed to load client config for "${id}":`, error);
      setClientConfig(null);
      setConfigError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsLoading(false);
    }
  }

  const value = {
    clientId,
    clientConfig,
    configError,
    isLoading,
    reloadConfig: () => loadClientConfig(clientId),
  };

  return <ClientContext.Provider value={value}>{children}</ClientContext.Provider>;
}

export function useClient() {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within ClientProvider');
  }
  return context;
}
